import { EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, ViewChild, ChangeDetectorRef, Directive } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import {Subscription} from 'rxjs';

/**
 * This component establishes tabs for a page. To use it, give it the following parameters
 * @input tabNames is an array of strings that are the names for each tab
 * @input startingIndex is the starting index of the tabs. Defaults to 0.
 * @input setIndex is an event  emitter the emits the index you want to select when you want to. Defaults to null.
 * @input height is the height of the tabs. Defaults to 48.
 * @input isChildTabs: if true, sets the styling to child tab styling
 * @input isGrandchildTab: if true, sets the styling to grandchild tab styling
 * @input disabledTabs: the names of tabs that display as disabled
 *
 * It provides you with the following
 * @output tabChange is a eventEmitter that gives you a string containing the name of the currently selected tab
 * @output selectionsChangeIndex is an eventEmitter of a number that emits the selected index upon selection
 */
@Directive()
export class Tabs implements OnInit, OnDestroy, OnChanges {

	// Properties
	@Input() tabNames: string[];
	@Input() startingIndex = 0;
	@Input() setIndex: EventEmitter<number>;
	@Input() noMargin: boolean; // deprecated
	@Input() forGradebook: boolean; // deprecated
	@Input() isChildTabs: boolean;
	@Input() isGrandchildTab: boolean;
    @Input() disabledTabs: string[];
	@Input() height = 48;
	@Input() locked = false;
	@Output() selectionsChange: EventEmitter<boolean[]> = new EventEmitter<boolean[]>(); // deprecated
	@Output() selectionsChangeIndex: EventEmitter<number> = new EventEmitter<number>();
	@Output() tabChange: EventEmitter<string> = new EventEmitter<string>();
    selected = new FormControl(0);
	tabLabels: string[] = [];
	noEvent = false;
	@ViewChild(MatTabGroup, { static: true }) tabs: MatTabGroup;
	subscriptions: Subscription[] = [];
	changeDetector: ChangeDetectorRef;

	// Class Functions
	constructor(
		changeDetector: ChangeDetectorRef
	) {
		this.changeDetector = changeDetector;
	}

	// Event Functions
	ngOnInit() {
        // POST: initializes the component

        // Setting starting values
		if (this.startingIndex >= 0) {
			this.selected.setValue(this.startingIndex);
        }

        // Responding to a selected index
        if (this.setIndex) {
        	this.subscriptions.push(
				this.setIndex.subscribe(index => {
					this.selected.setValue(index);
					this.changeDetector.detectChanges();
				})
			);
		}

		// Sets the tab names
        if (this.tabNames) {
            this.tabLabels = [];
            this.tabNames.forEach(tabName => {
                this.tabLabels.push(tabName.toLowerCase());
            });
        }
    }

    ngOnChanges() {
        // POST: resets the tab labels on changes
        if (this.tabNames) {
            this.tabLabels = [];
            this.tabNames.forEach(tabName => {
                this.tabLabels.push(tabName.toLowerCase());
            });
        }
    }

	ngOnDestroy() {
		this.subscriptions.forEach(subscription => {
			subscription.unsubscribe();
		});
	}

	selectedIndexChange(index: number) {
		if (this.noEvent) {
			this.noEvent = false;
		}
		else {
			this.selected.setValue(index);
			this.selectionsChangeIndex.emit(index);
			this.tabChange.emit(this.tabNames[index]);
		}
	}

	selectTabByName(tabName: string) {
		const i = this.tabNames.findIndex(o => o == tabName);
		if (i >= 0) {
			this.selected.setValue(i);
		}
	}

	clickTabWithoutEvents(tabName: string) {
		let index = 0;
		for (const tab of this.tabNames) {
			if (tab == tabName) {
				this.noEvent = true;
				this.selected.setValue(index);
				break;
			}
			index = index + 1;
		}
	}

	checkDisabled(tab: string) {
		if (this.disabledTabs != null && this.disabledTabs.length > 0) {
			if (this.disabledTabs.filter(function(t) {return t.toLowerCase() == tab.toLowerCase(); }).length > 0) {
				return true;
			}
		}
		return false;
	}

}